"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useRef, useState } from "react";
import { Clock, Calendar, User, Mail, Phone, Stethoscope, MessageSquare, CircleCheck, CircleAlert, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "@/components/common/Link";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function AppointmentFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const inputClasses = "border-input placeholder:text-muted-foreground focus-visible:border-primary focus-visible:ring-primary/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex h-12 w-full rounded-xl border bg-secondary/30 px-4 py-2 text-base shadow-sm transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm font-sans";
  if (isSubmitted && !submitError) {
    return <div data-section-id="84975" className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
                <div className="mb-6 flex size-16 items-center justify-center rounded-full bg-secondary text-primary dark:bg-primary/20 dark:text-primary">
                    <CircleCheck className="size-8" />
                </div>
                <h3 className="mb-2 text-2xl font-serif font-semibold text-foreground">Request Received!</h3>
                <p className="mb-8 max-w-md text-muted-foreground font-sans">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" size="lg" className="rounded-full border-primary text-primary hover:bg-primary hover:text-white">
                    Book Another Consultation
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {}
                <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="flex items-center gap-2 text-foreground font-medium">
                                <User className="size-4 text-primary" />
                                Full Name
                            </FormLabel>
                            <FormControl>
                                <input type="text" placeholder="Jane Smith" className={inputClasses} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                {}
                <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^\S+@\S+$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="flex items-center gap-2 text-foreground font-medium">
                                <Mail className="size-4 text-primary" />
                                Email Address
                            </FormLabel>
                            <FormControl>
                                <input type="email" placeholder="jane@example.com" className={inputClasses} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                {}
                <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="flex items-center gap-2 text-foreground font-medium">
                                <Phone className="size-4 text-primary" />
                                Phone Number
                            </FormLabel>
                            <FormControl>
                                <input type="tel" placeholder="(626) 555-0123" className={inputClasses} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                {}
                <FormField control={control} name="service" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="flex items-center gap-2 text-foreground font-medium">
                                <Stethoscope className="size-4 text-primary" />
                                Treatment Interest
                            </FormLabel>
                            <FormControl>
                                <select className={inputClasses} {...field}>
                                    <option value="" disabled>Select a treatment</option>
                                    <option value="Custom Dentures">Custom Dentures</option>
                                    <option value="Dental Implants">Dental Implants</option>
                                    <option value="Tooth Extraction">Tooth Extraction</option>
                                    <option value="Sedation Dentistry">Sedation Dentistry</option>
                                    <option value="Same-Day Repairs">Same-Day Repairs</option>
                                    <option value="General Exam">General Exam</option>
                                </select>
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                {}
                <FormField control={control} name="preferred_date" rules={{
        required: "Please select a preferred date"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="flex items-center gap-2 text-foreground font-medium">
                                <SafeHydrate><Calendar className="size-4 text-primary" /></SafeHydrate>
                                Preferred Date
                            </FormLabel>
                            <FormControl>
                                <input type="date" className={inputClasses} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                {}
                <FormField control={control} name="preferred_time" rules={{
        required: "Please select a preferred time"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="flex items-center gap-2 text-foreground font-medium">
                                <Clock className="size-4 text-primary" />
                                Preferred Time
                            </FormLabel>
                            <FormControl>
                                <select className={inputClasses} {...field}>
                                    <option value="" disabled>Select a time</option>
                                    <option value="09:00 AM">09:00 AM</option>
                                    <option value="10:00 AM">10:00 AM</option>
                                    <option value="11:00 AM">11:00 AM</option>
                                    <option value="12:00 PM">12:00 PM</option>
                                    <option value="01:00 PM">01:00 PM</option>
                                    <option value="02:00 PM">02:00 PM</option>
                                    <option value="03:00 PM">03:00 PM</option>
                                    <option value="04:00 PM">04:00 PM</option>
                                    <option value="05:00 PM">05:00 PM</option>
                                </select>
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                {}
                <FormField control={control} name="message" render={({
        field
      }) => <FormItem className="md:col-span-2">
                            <FormLabel className="flex items-center gap-2 text-foreground font-medium">
                                <MessageSquare className="size-4 text-primary" />
                                How can we help?
                            </FormLabel>
                            <FormControl>
                                <Textarea placeholder="Tell us about your dental goals or any specific concerns you have..." className="min-h-[120px] resize-y rounded-xl bg-secondary/30 border-input focus-visible:ring-primary/20 font-sans" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            {submitError && <div className="flex items-center gap-3 rounded-xl border border-destructive/50 bg-destructive/10 p-4 text-destructive">
                    <CircleAlert className="size-5 shrink-0" />
                    <p className="text-sm font-sans">{submitError}</p>
                </div>}

            <div className="pt-4">
                <Button asChild size="lg" className="w-full md:w-auto md:min-w-[240px] rounded-full bg-primary text-primary-foreground hover:scale-[1.02] transition-transform shadow-lg shadow-primary/20">
                    <Link to="tel:626-268-5990">
                        Request Consultation
                    </Link>
                </Button>
                <p className="mt-4 text-center text-xs text-muted-foreground md:text-left font-sans">
                    By submitting this form, you agree to our privacy policy. Our El Monte team will contact you shortly to confirm your specific appointment details.
                </p>
            </div>
        </Form>;
}
export default function BookYourAppointment() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section id="request-an-appointment" className="bg-background py-16 md:py-24 lg:py-32">
            <div className="container mx-auto px-4 md:px-6">
                <div ref={ref} className={`mx-auto max-w-4xl transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
                    {}
                    <div className="mb-10 text-center md:mb-16">
                        <h2 className="mb-4 text-3xl font-serif font-semibold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-6xl">
                            Request Your Consultation
                        </h2>
                        <div className="mx-auto flex max-w-2xl items-center justify-center gap-2 rounded-full bg-secondary px-6 py-2 text-sm font-medium text-secondary-foreground shadow-sm">
                            <Clock className="size-4" />
                            <span>Same-day responses are common during clinical hours.</span>
                        </div>
                        <p className="mt-6 text-lg text-muted-foreground font-sans max-w-3xl mx-auto">
                            Start your journey to a confident smile at Smart Choice Dentures & Implants. Fill out the form below, and our expert El Monte team will reach out to schedule your visit.
                        </p>
                    </div>

                    {}
                    <div className="overflow-hidden rounded-2xl border border-border/40 bg-card p-6 shadow-2xl shadow-shadow-color/5 sm:p-8 md:p-12 backdrop-blur-sm">
                        <FormProvider formKey="appointment_form_contact_us" formId="appointment_form_contact_us" sectionName="Request An Appointment" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            service: "",
            preferred_date: "",
            preferred_time: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            service: {
              required: true
            },
            preferred_date: {
              required: true
            },
            preferred_time: {
              required: true
            }
          }} submitText="Request Consultation" successMessage="Thank you! Your request has been received. A member of the Smart Choice team will contact you shortly to finalize your appointment." errorMessage="We encountered an issue submitting your request. Please try again or call our El Monte office directly.">
                            <AppointmentFormInner />
                        </FormProvider>
                    </div>
                    
                    {}
                    <div className="mt-10 flex flex-col items-center justify-center gap-4 text-center sm:flex-row sm:gap-12">
                        <div className="flex items-center gap-3 text-muted-foreground font-sans">
                            <div className="flex size-10 items-center justify-center rounded-full bg-secondary/50">
                                <Phone className="size-5 text-primary" />
                            </div>
                            <span>Urgent assistance? Call <strong className="text-foreground">626-268-5990</strong></span>
                        </div>
                        <div className="hidden h-8 w-px bg-border/60 sm:block"></div>
                        <div className="flex items-center gap-3 text-muted-foreground font-sans">
                            <div className="flex size-10 items-center justify-center rounded-full bg-secondary/50">
                                <Clock className="size-5 text-primary" />
                            </div>
                            <span>By Appointment Only<br />Mon–Fri: 9:00 AM – 5:00 PM</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>;
}